import React, { Component } from 'react'
import PropTypes from 'prop-types'
import Button from '@material-ui/core/Button'
import IconButton from '@material-ui/core/IconButton'
import DeleteIcon from '@material-ui/icons/Delete'
import TextField from '@material-ui/core/TextField'
import TickIcon from '@material-ui/icons/Check'
import CloseIcon from '@material-ui/icons/Close'
import AddIcon from '@material-ui/icons/Add'
import EditIcon from '@material-ui/icons/Edit'

import StyleSheet from 'styles'
import { capitalize, uuid } from 'utils'
import { ConditionalRender } from 'components'
import Table, { TableHead, TableBody, TableRow, TableCell } from 'components/CollapsibleTable'
import FormComponentsMixin from 'containers/shared/FormComponentsMixin'

export class CollectionEditForm extends FormComponentsMixin(Component) {
  static propTypes = {
    elements: PropTypes.array.isRequired,
    onElementsChange: PropTypes.func.isRequired,
    newElementBuilder: PropTypes.func,
    attributes: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      required: PropTypes.bool,
      renderer: PropTypes.func,
      input: PropTypes.func,
      validator: PropTypes.func
    })).isRequired,
  }

  constructor(props){
    super(props)
    this.state = {
      editing: false,
      attributes: {},
      errors: {}
    }
  }

  get elements(){
    return this.props.elements
  }

  getError = attribute =>
    this.state.errors[attribute]

  hasError = attribute =>
    !!this.state.errors[attribute]

  changeHandler = attribute => ({target: { value }}) =>
    this.setState({attributes: {...this.state.attributes, [attribute]: value}})

  handleEdit = () =>
    this.setState({editing: true, attributes: {}, errors: {}})

  handleStopEdit = () =>
    this.setState({editing: false})

  handleAddElement = () => {
    if(!this.validate())
      return
    const newElement = (this.props.newElementBuilder) ? this.props.newElementBuilder(this.state.attributes) : this.state.attributes
    if(!newElement.id){
      newElement.uuid = newElement.uuid || uuid()
    }
    this.props.onElementsChange(this.elements.filter(({id, uuid}) => id !== newElement.id || uuid !== newElement.uuid).concat([newElement]))
    this.setState({attributes: {}, editing: false})
  }

  handleDeleteElement = idx => () => {
    const elements = [...this.elements]
    elements.splice(idx, 1)
    this.props.onElementsChange(elements)
  }

  handleEditElement = idx => () => {
    const element = this.elements[idx]
    this.setState({editing: true, attributes: {...element}, errors: {}})
  }

  validate = () => {
    const errors = {};
    this.props.attributes.forEach(({name, required, validator}) => {
      const attributeValue = this.state.attributes[name] || ''
      const attributeValueType = typeof attributeValue
      const isEmpty = () => {
        switch(attributeValueType){
          case 'string':
            return !attributeValue.trim('')
          default:
            return !attributeValue
        }
      }

      if(!!validator) {
        let fieldErrors = validator(name, required, this)
        Object.assign(errors, fieldErrors)
      } else if(required && isEmpty()){
        errors[name] = 'Must be present'
      }
    })
    this.setState({errors})
    return (Object.keys(errors).length === 0)
  }

  attributesFor = attribute => {
    return {
      label:     capitalize(attribute),
      onChange:  this.changeHandler(attribute),
      error:     this.hasError(attribute),
      helperText:this.getError(attribute),
      value:     this.state.attributes[attribute] || '',
      fullWidth: true
    }
  }

  EditForm = () => {
    return (
      <div>
        {
          this.props.attributes.map((element, index) => {
            const {name, input} = element
            return (input) ? input(element, this) : <TextField key={name} autoFocus={(index === 0)} {...this.attributesFor(name)}/>
          })
        }
        <div className={styles.editButtons}>
          <Button onClick={this.handleStopEdit}>
            <CloseIcon/>
          </Button>
          <Button onClick={this.handleAddElement}>
            <TickIcon/>
          </Button>
        </div>
      </div>
    )
  }

  renderRow = (element, index) => {
    const attributesCells = this.props.attributes.map(({name, renderer}) => {
      const renderedAttribute = (renderer) ? renderer(element) : element[name]
      return <TableCell key={index} className={styles.cell}>
        {renderedAttribute}
      </TableCell>
    })

    return (
      <TableRow key={index}>
        {attributesCells}
        <TableCell className={styles.cell}>
          <IconButton aria-label="delete" onClick={ this.handleDeleteElement(index) }>
            <DeleteIcon />
          </IconButton>
          <IconButton aria-label="edit" onClick={ this.handleEditElement(index) }>
            <EditIcon />
          </IconButton>
        </TableCell>
      </TableRow>
    )
  }

  render = () => {
    return (
      <div>
        <Table breakpoint={600}>
          <TableHead>
            <TableRow>
              {
                this.props.attributes.map(({name, label}) => {
                  return <TableCell key={name}>{label}</TableCell>
                })
              }
              <TableCell style={{width: 100}}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {this.elements.map(this.renderRow)}
          </TableBody>
        </Table>
        <div className={styles.tableActions}>
          <ConditionalRender onClick={this.handleEdit} requires={!this.state.editing} component={Button} variant='fab' color='primary'>
            <AddIcon/>
          </ConditionalRender>
        </div>
        <ConditionalRender requires={this.state.editing} component={this.EditForm}/>
      </div>
    )
  }
}

const styles = StyleSheet({
  tableActions: {
    justifyContent: 'flex-end',
    flexDirection: 'row',
    display: 'flex'
  },
  editButtons: {
    justifyContent: 'flex-end',
    flexDirection: 'row',
    display: 'flex',
    flexWrap: 'wrap'
  },
  cell: {
    padding: 0,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    maxWidth: 50
  }
}, {classNamePrefix: 'CollectionEditForm-'})

export default CollectionEditForm